import { useState } from "react";

import {
    Box,
    Button,
    HStack,
    Separator,
    Text,
    VStack,
    Dialog,
    Flex,
} from "@chakra-ui/react";

import {
    Trash2,
} from "lucide-react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    requestAccountDeletionAction,
} from "../../../state/actions/authActions";

import {
    toaster,
} from "../../../components/toaster";

import ActivityToast from "../../../components/ActivityToast";

import Card from "../../Dashboard/components/Card";

const ActionRow = ({
    icon,
    title,
    description,
    buttonLabel,
    buttonColor = "#2453FF",
    buttonVariant = "outline",
    onClick,
    loading = false,
}) => (
    <>
        <Flex
            direction="column"
            py={4}
            gap={4}
        >
            <HStack
                align="flex-start"
                gap={4}
                w="100%"
            >
                <Box
                    bg="#FEF2F2"
                    color="#DC2626"
                    p={3}
                    borderRadius="12px"
                    flexShrink={0}
                >
                    {icon}
                </Box>

                <Box flex="1">
                    <Text
                        fontWeight="600"
                        color="#071D54"
                    >
                        {title}
                    </Text>

                    <Text
                        mt={1}
                        fontSize="sm"
                        color="gray.500"
                        lineHeight="1.7"
                    >
                        {description}
                    </Text>
                </Box>
            </HStack>

            <Button
                alignSelf="center"
                w={{
                    base: "100%",
                    md: "220px",
                }}
                minH={{
                    base: "48px",
                    md: "44px",
                }}
                variant={buttonVariant}
                loading={loading}
                loadingText="Submitting..."
                onClick={onClick}
                color={
                    buttonVariant === "solid"
                        ? "white"
                        : buttonColor
                }
                bg={
                    buttonVariant === "solid"
                        ? buttonColor
                        : "transparent"
                }
                borderColor={buttonColor}
                _hover={{
                    bg:
                        buttonVariant === "solid"
                            ? "#B91C1C"
                            : "#FEF2F2",
                }}
            >
                {buttonLabel}
            </Button>
        </Flex>

        <Separator />
    </>
);

const AccountManagement = () => {
    const dispatch =
        useDispatch();

    const {
        deletingAccount,
    } = useSelector(
        state =>
            state.auth
    );

    const [
        isDeleteOpen,
        setDeleteOpen,
    ] = useState(false);

    const handleDeletionRequest =
        async () => {
            const result =
                await dispatch(
                    requestAccountDeletionAction()
                );

            if (
                result?.success
            ) {
                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="success"
                            title="Deletion Requested"
                            description="Your account deletion request has been submitted successfully. Our team will review and process it shortly."
                            timestamp="Just now"
                        />
                    ),
                });
            } else {
                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="error"
                            title="Request Failed"
                            description={
                                result?.error ||
                                "Unable to submit your deletion request."
                            }
                            timestamp="Just now"
                        />
                    ),
                });
            }
        };

    return (
        <Card>
            <Text
                fontSize="xl"
                fontWeight="700"
                color="#071D54"
                mb={2}
            >
                Account Management
            </Text>

            <Text
                color="gray.500"
                fontSize="sm"
                mb={6}
            >
                Manage your Driver Partner account,
                active sessions and account status.
            </Text>

            <VStack
                align="stretch"
                gap={0}
            >
                <ActionRow
                    icon={
                        <Trash2
                            size={20}
                        />
                    }
                    title="Request Account Deletion"
                    description="Submit a request to permanently delete your partner account and associated data. Our team will review and process your request. This action cannot be undone once approved."
                    buttonLabel="Request Deletion"
                    buttonColor="#DC2626"
                    buttonVariant="solid"
                    onClick={() =>
                        setDeleteOpen(
                            true
                        )
                    }
                    loading={
                        deletingAccount
                    }
                />
            </VStack>

            <Dialog.Root
                open={
                    isDeleteOpen
                }
                onOpenChange={(
                    e
                ) =>
                    setDeleteOpen(
                        e.open
                    )
                }
            >
                <Dialog.Backdrop />

                <Dialog.Positioner
                    p={{
                        base: 4,
                        md: 6,
                    }}
                >
                    <Dialog.Content
                        w="100%"
                        maxW={{
                            base: "95vw",
                            md: "500px",
                        }}
                        borderRadius={{
                            base: "20px",
                            md: "24px",
                        }}
                    >
                        <Dialog.Header>
                            <Dialog.Title>
                                Confirm Account
                                Deletion
                                Request
                            </Dialog.Title>
                        </Dialog.Header>

                        <Dialog.Body>
                            <Text
                                color="gray.600"
                                lineHeight="tall"
                            >
                                Are you
                                sure you
                                want to
                                submit an
                                account
                                deletion
                                request?
                            </Text>

                            <Text
                                mt={4}
                                color="gray.600"
                                lineHeight="tall"
                            >
                                Once
                                approved
                                by our
                                team,
                                your
                                partner
                                account
                                and
                                associated
                                data may
                                be
                                permanently
                                removed
                                and this
                                action
                                cannot
                                be
                                undone.
                            </Text>
                        </Dialog.Body>

                        <Dialog.Footer>
                            <Flex
                                direction={{
                                    base: "column-reverse",
                                    sm: "row",
                                }}
                                gap={3}
                                w="100%"
                            >
                               <Button
                                flex="1"
                                w="100%"
                                minH={{
                                    base: "44px",
                                    md: "40px",
                                }}
                                variant="outline"
                                onClick={() =>
                                    setDeleteOpen(
                                        false
                                    )
                                }
                            >
                                Cancel
                            </Button>

                            <Button
                                flex="1"
                                w="100%"
                                minH={{
                                    base: "44px",
                                    md: "40px",
                                }}
                                bg="#DC2626"
                                color="white"
                                loading={
                                    deletingAccount
                                }
                                loadingText="Submitting..."
                                _hover={{
                                    bg: "#B91C1C",
                                }}
                                onClick={async () => {
                                    await handleDeletionRequest();

                                    setDeleteOpen(
                                        false
                                    );
                                }}
                            >
                                Confirm Request
                            </Button>
                            </Flex>
                        </Dialog.Footer>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Dialog.Root>
        </Card>
    );
};

export default AccountManagement;